import { Category, Product } from '@gozoki/api-types';
import { Button, CircularProgress, LinearProgress, Menu, MenuItem, Skeleton } from '@mui/material';
import { useContext, useState } from 'react';
import { ROUTES, ROUTE_BUILDER } from '../constants/routes.constants';

import { frenchFormat } from '@gozoki/tools';
import { Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import styles from '../../assets/styles/DashboardPage.module.css';
import { AuthContext } from '../context/auth.context';
import { ThemeColors } from '../theme/colors.theme';

export const DashboardCard = ({
    label,
    bigValue,
    smallValue,
    percent,
    orange,
    smallOrange,
    details,
    negativePercent,
    download,
    downloadPdf,
    downloadCsv,
    isDownloading = false,
    isLoading = false,
    noMargin = false,
}: {
    label: string;
    bigValue: string;
    smallValue?: string;
    percent?: number;
    orange?: boolean;
    smallOrange?: boolean;
    details?: () => void;
    negativePercent?: boolean;
    download?: () => void;
    downloadPdf?: () => void;
    downloadCsv?: () => void;
    isDownloading?: boolean;
    isLoading?: boolean;
    noMargin?: boolean;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (downloadPdf || downloadCsv) {
            setAnchorEl(event.currentTarget);
        } else if (download) {
            download();
        }
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handlePdfDownload = () => {
        handleCloseMenu();
        if (downloadPdf) downloadPdf();
    };

    const handleCsvDownload = () => {
        handleCloseMenu();
        if (downloadCsv) downloadCsv();
    };

    return (
        <div className={styles.smallWhiteCards} style={{ margin: noMargin ? 0 : 4 }}>
            {download || downloadPdf || downloadCsv ? (
                <div className={styles.smallWhiteCardsDownload}>
                    <Button onClick={handleDownloadClick} disabled={isDownloading || isLoading}>
                        {isDownloading ? <CircularProgress size={20} /> : <Download />}
                    </Button>
                    {(downloadPdf || downloadCsv) && (
                        <Menu anchorEl={anchorEl} open={openMenu} onClose={handleCloseMenu}>
                            {downloadPdf && (
                                <MenuItem onClick={handlePdfDownload}>Télécharger en PDF</MenuItem>
                            )}
                            {downloadCsv && (
                                <MenuItem onClick={handleCsvDownload}>Télécharger en CSV</MenuItem>
                            )}
                        </Menu>
                    )}
                </div>
            ) : null}
            <div className={styles.smallWhiteCardContainer}>
                <div className={styles.smallWhiteCardLeft}>
                    <p className={styles.smallWhiteCardsTitle}>{label}</p>

                    {isLoading ? (
                        <>
                            <Skeleton
                                variant="text"
                                width="60%"
                                height={40}
                                sx={{ fontSize: '2rem' }}
                            />
                            <Skeleton variant="text" width="40%" height={20} />
                        </>
                    ) : (
                        <>
                            <p
                                className={
                                    orange
                                        ? styles.smallWhiteCardsMainValueOrange
                                        : styles.smallWhiteCardsMainValue
                                }
                            >
                                {bigValue}
                            </p>
                            <p
                                className={styles.smallWhiteCardsSecondValue}
                                style={{ color: smallOrange ? ThemeColors.ORANGE : 'black' }}
                            >
                                {smallValue ?? ''}
                            </p>
                        </>
                    )}
                    <p className={styles.smallWhiteCardsDetails}>
                        {details && !isLoading ? (
                            <Button onClick={details}>Voir détails</Button>
                        ) : null}
                    </p>
                </div>
                <div className={styles.smallWhiteCardRight}>
                    {isLoading ? <Skeleton variant="circular" width={24} height={24} /> : null}
                    {percent && !isLoading ? (
                        <span
                            className={
                                percent > 0 || (negativePercent && percent < 0)
                                    ? styles.smallWhiteCardsIconGood
                                    : styles.smallWhiteCardsIconBad
                            }
                        >{`${frenchFormat(percent * 100)}\u00A0%`}</span>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export interface DashboardInfoCardProps {
    label: string;
    info: string;
    noMargin?: boolean;
}

export const DashboardInfoCard = ({ label, info, noMargin = false }: DashboardInfoCardProps) => {
    return (
        <div className={styles.smallWhiteCards} style={{ margin: noMargin ? 0 : 4 }}>
            <div className={styles.smallWhiteCardContainer}>
                <div className={styles.smallWhiteCardLeft}>
                    {label && <p className={styles.smallWhiteCardsTitle}>{label}</p>}
                    <p className={styles.smallWhiteCardsMainValue}>{info}</p>
                    {/* <p className={styles.smallWhiteCardsDetails}>
                        {details ? <Button onClick={details}>Voir détails</Button> : null}
                    </p> */}
                </div>
            </div>
        </div>
    );
};

export interface DashboardActionCard {
    label: string;
    subLabel: string;
    actionLabel: string;
    action: () => void;
    disabled?: boolean;
}

export const DashboardActionCard = ({
    label,
    subLabel,
    actionLabel,
    action,
    disabled = false,
}: DashboardActionCard) => {
    return (
        <div className={styles.smallWhiteCards}>
            <div className={styles.smallWhiteCardContainer}>
                <div className={styles.smallWhiteCardLeft} style={{ position: 'relative' }}>
                    {label && <p className={styles.smallWhiteCardsTitle}>{label}</p>}
                    {subLabel && <p className={styles.smallWhiteCardsSecondValue}>{subLabel}</p>}
                    <Button
                        variant="contained"
                        sx={{
                            position: 'absolute',
                            bottom: '5px',
                            right: '5px',
                        }}
                        onClick={action}
                        disabled={disabled}
                    >
                        {actionLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const ProductCard = ({
    product,
    count,
    ca,
    mode,
    maxCount,
    maxCa,
    totalCount = 1,
    totalCa = 1,
}: {
    product: Product;
    count: number;
    ca: number;
    mode: 'count' | 'ca';
    maxCount: number;
    maxCa: number;
    totalCount: number;
    totalCa: number;
}) => {
    const navigate = useNavigate();

    if (!product) return null;

    const handleClick = () => {
        navigate(ROUTE_BUILDER.productDetails(product.id));
    };

    return (
        <Button onClick={handleClick} style={{ all: 'unset', width: '100%', cursor: 'pointer' }}>
            <div className={styles.famousThingsContainer}>
                <div className={styles.famousThingsImageContainer}>
                    {product.optimizedPreviewImageUrl ? (
                        <img
                            src={product.optimizedPreviewImageUrl}
                            alt={product?.label}
                            className={styles.famousThingsImage}
                        />
                    ) : (
                        <div className={styles.famousThingsFakeImage} />
                    )}
                </div>
                <div className={styles.famousThingsTextContainer}>
                    <p className={styles.famousThingsLabel}>{product?.label}</p>
                    <p className={styles.famousThingsRef}>{product.reference}</p>
                </div>
                <div className={styles.famousThingsCountContainer}>
                    <p className={styles.famousThingsLabel}>
                        {mode === 'count' ? count : `${ca}\u00A0€`}
                    </p>
                    <p className={styles.famousThingsRef}>
                        {mode === 'count'
                            ? `${frenchFormat((count / totalCount) * 100)}\u00A0%`
                            : `${frenchFormat((ca / totalCa) * 100)}\u00A0%`}
                    </p>
                </div>
            </div>
            <div className={styles.famousThingsLinearContainer}>
                <LinearProgress
                    variant="determinate"
                    color="secondary"
                    value={mode === 'count' ? (count / maxCount) * 100 : (ca / maxCa) * 100}
                />
            </div>
        </Button>
    );
};

export const CategoryCard = ({
    category,
    count,
    ca,
    mode,
    maxCount,
    maxCa,
    totalCount = 1,
    totalCa = 1,
}: {
    category: Category;
    count: number;
    ca: number;
    mode: 'count' | 'ca';
    maxCount: number;
    maxCa: number;
    totalCount: number;
    totalCa: number;
}) => {
    const { hasRights } = useContext(AuthContext);
    const navigate = useNavigate();

    if (!category) return null;

    const isAdmin = hasRights(['ADMIN']);

    const handleCategoryClick = () => {
        navigate(`${ROUTES.CATEGORIES}/${ROUTE_BUILDER.updateCategory(category.id)}`);
    };

    if (category.isSubCategory) {
        return (
            <div className={styles.famousSmallThingsContainer}>
                <div className={styles.famousSmallThingsImageContainer}>
                    {category.image?.url ? (
                        <img
                            src={category.image?.url}
                            alt={category.name}
                            className={styles.famousSmallThingsImage}
                        />
                    ) : (
                        <div className={styles.famousSmallThingsFakeImage} />
                    )}
                </div>
                <div className={styles.famousSmallThingsTextContainer}>
                    <p className={styles.famousSmallThingsLabel}>{category.name}</p>
                    <p className={styles.famousSmallThingsRef}>{category.description}</p>
                </div>
                <div className={styles.famousSmallThingsCountContainer}>
                    <p className={styles.famousSmallThingsLabel}>
                        {mode === 'count' ? `${count}` : `${ca}\u00A0€`}
                    </p>
                    <p className={styles.famousSmallThingsRef}>
                        {mode === 'count'
                            ? `(${frenchFormat((count / totalCount) * 100)}\u00A0%)`
                            : `(${frenchFormat((ca / totalCa) * 100)}\u00A0%)`}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <Button
                onClick={handleCategoryClick}
                style={{
                    all: 'unset',
                    width: '100%',
                    cursor: isAdmin ? 'pointer' : 'auto',
                }}
                disabled={!isAdmin}
            >
                <div className={styles.famousThingsContainer}>
                    <div className={styles.famousThingsImageContainer}>
                        {category.image?.url ? (
                            <img
                                src={category.image?.url}
                                alt={category.name}
                                className={styles.famousThingsImage}
                            />
                        ) : (
                            <div className={styles.famousThingsFakeImage} />
                        )}
                    </div>
                    <div className={styles.famousThingsTextContainer}>
                        <p className={styles.famousThingsLabel}>{category.name}</p>
                        <p className={styles.famousThingsRef}>{category.description}</p>
                    </div>
                    <div className={styles.famousThingsCountContainer}>
                        <p className={styles.famousThingsLabel}>
                            {mode === 'count' ? count : `${ca}\u00A0€`}
                        </p>
                        <p className={styles.famousThingsRef}>
                            {mode === 'count'
                                ? `${frenchFormat((count / totalCount) * 100)}\u00A0%`
                                : `${frenchFormat((ca / totalCa) * 100)}\u00A0%`}
                        </p>
                    </div>
                </div>
            </Button>
            <div className={styles.famousThingsLinearContainer}>
                <LinearProgress
                    variant="determinate"
                    color="secondary"
                    value={mode === 'count' ? (count / maxCount) * 100 : (ca / maxCa) * 100}
                />
            </div>
        </>
    );
};
